import { Activity, ActivityType, GenerateType } from 'models/Activity';
import React from 'react';
import { BuildCourseContext } from 'contextProviders/BuildCourseContext';
import strings from 'strings/strings.json';
import { FlexColSm, Headline3 } from 'components/Elements';
import useNeveForm from 'components/forms/NeveForm';
import { useFieldArray, useWatch } from 'react-hook-form';
import TextField from 'components/forms/TextField';
import { createErrorMessage, fieldErrorCodes, requiredFieldErrorMessage } from 'shared/error-messages';
import Button from 'components/buttons/Button';
import AddIcon from 'assets/icons/controls/AddIcon';
import useModifyRequest from 'hooks/useModifyRequest';
import DeleteActivityAlert from './shared/DeleteActivityAlert';
import { ModuleOnlyHasOneTask as ModuleOnlyHasOneActivity } from './shared/SharedEditTaskComponents';
import { Course } from 'models/Course';
import { ActivityQuestion } from 'models/ActivityQuestion';
import { ActivityFormState } from '../shared/SharedActivityFormComponents';
import { EditActivityFormCard } from '../shared/EditActivityFormCard';
import {
    UpdateQuestionnaireInput,
    UpdateQuestionnaireRequest,
    UpdateQuestionFields,
    QuestionCard,
    ButtonContainer,
} from '../shared/SharedQuestionnaireComponents';
import BaseDetailsForm from '../shared/DetailsForm';
import TextAreaField from 'components/forms/TextAreaField';
import Notification, { NotificationType } from 'components/notifiers/Notification';

type EditQuestionnaireFormProps = {
    questionnaire: Activity;
    returnToCourseStructure: () => void;
};

export default function EditQuestionnaireForm({
    questionnaire,
    returnToCourseStructure,
}: EditQuestionnaireFormProps): JSX.Element {
    const { course, editMode, refetchCourse, setCourseState, setActiveStep } = React.useContext(BuildCourseContext);
    const [error, setError] = React.useState('');
    const [state, setState] = React.useState<ActivityFormState>('clean');
    const [deleteAlertOpen, setDeleteAlertOpen] = React.useState(false);

    const { modifyData: updateQuestionnaire, loading: submitting } = useModifyRequest(
        `activities/questionnaire/${questionnaire.id}`,
        'PUT',
    );

    const sortedQuestions = React.useMemo(
        () => [...questionnaire.questions].sort((a, b) => a.order - b.order),
        [questionnaire.questions],
    );

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        watch,
        reset,
    } = useNeveForm<UpdateQuestionnaireInput>({
        title: questionnaire.title,
        description: questionnaire.description,
        durationInMins: questionnaire.durationInMins,
        questions: sortedQuestions,
    });

    const watchedTitle = watch('title');
    const watchedDescription = watch('description');
    const watchedDurationInMins = watch('durationInMins');
    const watchedQuestions = useWatch({
        control,
        name: 'questions',
    });

    const originalComparable = React.useMemo(
        () =>
            sortedQuestions.map((q) => ({
                question: q.question,
                hint: q.hint,
            })),
        [sortedQuestions],
    );

    React.useEffect(() => {
        const changed =
            watchedTitle !== questionnaire.title ||
            watchedDescription !== questionnaire.description ||
            watchedDurationInMins !== questionnaire.durationInMins ||
            JSON.stringify(
                watchedQuestions.map((q) => ({
                    question: q.question,
                    hint: q.hint,
                })),
            ) !== JSON.stringify(originalComparable);

        setState(changed ? 'dirty' : 'clean');
    }, [watchedTitle, watchedDescription, watchedDurationInMins, watchedQuestions]); // eslint-disable-line

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'questions',
    });

    function addNewQuestion() {
        append({ question: '', hint: '' });
    }

    function removeQuestion(index: number) {
        remove(index);
    }

    //GENERATED QUESTIONNAIRE DOESN't need to add a question

    async function submit(formData: UpdateQuestionnaireInput) {
        setError('');

        if (!formData.questions.length) {
            setError(strings.questionnaireForm.error.addQuestion);
            return;
        }

        if (editMode) {
            setCourseState((prev) => {
                const copy = structuredClone(prev) as Course;
                const updatedQuestionnaire = copy.modules
                    .flatMap((m) => m.activities)
                    .find((a) => a.id === questionnaire.id);

                if (!updatedQuestionnaire) return copy;

                updatedQuestionnaire.title = formData.title;
                updatedQuestionnaire.description = formData.description;
                updatedQuestionnaire.durationInMins = formData.durationInMins ?? null;
                updatedQuestionnaire.questions = formData.questions.map((question, index) => {
                    const updatedQuestion: ActivityQuestion = {
                        id: question.id ?? '',
                        question: question.question,
                        hint: question.hint,
                        order: index,
                        answers: [],
                    };
                    return updatedQuestion;
                });
                return copy;
            });

            reset(formData);
            setState('saved');
            return;
        }

        const request: UpdateQuestionnaireRequest = {
            title: formData.title,
            description: formData.description,
            durationInMins: formData.durationInMins,
            questions: formData.questions.map<UpdateQuestionFields>((q) => ({
                id: q.id,
                question: q.question,
                hint: q.hint,
            })),
        };

        const response = await updateQuestionnaire(request);
        const { errors } = response;

        if (errors) {
            setError(strings.questionnaireForm.error.edit);
            return;
        }

        reset(formData);
        await refetchCourse();
        setState('saved');
        return;
    }

    const generatedQuestionnaireApproved =
        questionnaire.type === ActivityType.QUESTIONNAIRE &&
        (!questionnaire.generateContentStatus ||
            (questionnaire.generateContentStatus.generateType === GenerateType.MODULE_QUESTIONNAIRE &&
                questionnaire.generateContentStatus.status === 'APPROVED'));

    if (!generatedQuestionnaireApproved) {
        return (
            <Notification
                notificationType={NotificationType.INFO}
                title="This questionnaire requires your approval."
                description="Neve is generating this questionnaire. Approve the questionnaire in the Generated content step. Once approved, you can edit the questionnaire."
                buttonToHelp={{
                    text: 'View generated content',
                    aria: 'View generated content',
                    action: () => {
                        setActiveStep('courseAIGeneratedContent');
                    },
                }}
            />
        );
    }

    const Questions = (
        <FlexColSm>
            <Headline3>{strings.questionnaireForm.questionsTitle}</Headline3>
            {fields.map((question, index) => {
                let questionErrorMessage = '';
                let hintTextErrorMessage = '';

                if (errors.questions && errors.questions[index] !== undefined) {
                    questionErrorMessage = errors.questions[index]?.question?.message ?? '';
                    hintTextErrorMessage = errors.questions[index]?.hint?.message ?? '';
                }

                return (
                    <QuestionCard key={index}>
                        <TextField
                            fieldName={`question-${index}`}
                            labelText={strings.questionnaireForm.questionInputLabel}
                            inputAria={strings.questionnaireForm.questionInputAria}
                            errorMessage={questionErrorMessage}
                            required
                            {...register(`questions.${index}.question`, {
                                required: {
                                    value: true,
                                    message: requiredFieldErrorMessage(strings.questionnaireForm.questionInputLabel),
                                },
                                maxLength: {
                                    value: 2000,
                                    message: createErrorMessage(
                                        strings.questionnaireForm.questionInputLabel,
                                        fieldErrorCodes.maxLength,
                                    ),
                                },
                            })}
                        />

                        <TextAreaField
                            fieldName={strings.questionnaireForm.hintInputLabel}
                            errorMessage={hintTextErrorMessage}
                            aria={strings.questionnaireForm.hintInputAria}
                            maxLength={2000}
                            {...register(`questions.${index}.hint`, {
                                maxLength: {
                                    value: 2000,
                                    message: createErrorMessage(
                                        strings.questionnaireForm.hintInputLabel,
                                        fieldErrorCodes.maxLength,
                                    ),
                                },
                            })}
                        />
                        <Button
                            title={strings.questionnaireForm.removeQuestionButton}
                            aria={strings.questionnaireForm.removeQuestionButtonAria}
                            onClick={() => removeQuestion(index)}
                            colour={'BLACK'}
                            disabled={submitting}
                        />
                    </QuestionCard>
                );
            })}
            <ButtonContainer>
                <Button
                    fullwidth
                    title={strings.questionnaireForm.addQuestionButton}
                    aria={strings.questionnaireForm.addQuestionButtonAria}
                    icon={<AddIcon />}
                    onClick={() => addNewQuestion()}
                    disabled={submitting}
                />
            </ButtonContainer>
        </FlexColSm>
    );

    const moduleOnlyHasOneActivity = ModuleOnlyHasOneActivity(course!, questionnaire.moduleId);

    return (
        <>
            <EditActivityFormCard
                title={strings.questionnaireForm.title.edit}
                onSubmit={handleSubmit(submit)}
                pageOne={<BaseDetailsForm register={register} errors={errors} autoFocus />}
                pageTwo={Questions}
                error={error}
                showLastActivityInModuleWarning={editMode && moduleOnlyHasOneActivity}
                showSavedSuccessfully={state === 'saved' && !editMode}
                showSavedSuccessfullyEditMode={state === 'saved' && editMode}
                setDeleteAlertOpen={() => setDeleteAlertOpen((prev) => !prev)}
                disableDeleteTaskButton={submitting || (editMode && moduleOnlyHasOneActivity)}
                disableSubmitButton={submitting || (editMode && state === 'clean')}
                submitting={submitting}
                activityType={ActivityType.QUESTIONNAIRE}
            />
            {deleteAlertOpen && (
                <DeleteActivityAlert
                    taskId={questionnaire.id}
                    moduleId={module.id}
                    returnToCourseStructure={returnToCourseStructure}
                    setAlertOpen={setDeleteAlertOpen}
                />
            )}
        </>
    );
}
